import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from '@/components/common';
import type { FrontlineUserAdminDto, TemporaryPasswordDto } from '@/api/types';
import { useGenerateTemporaryPassword, useSendFrontlinePasswordReset, useSendFrontlineVerification } from '@/hooks/useFrontlineUsersAdmin';
import { frontlineErrorKey } from './frontlineErrors';

interface Props {
  user: FrontlineUserAdminDto;
  onDone: (message: string) => void;
  onError: (message: string) => void;
}

type Translate = (key: string) => string;

/**
 * Qué se puede hacer en la sección «Contraseña» y, si algo está apagado, POR QUÉ. El motivo se muestra como texto (no
 * solo como tooltip): un botón deshabilitado no debe parecer disponible ni obligar a pasar el mouse para enterarse.
 */
function passwordAvailability(user: FrontlineUserAdminDto, t: Translate) {
  // Cuenta del AD: entra con su cuenta corporativa, así que nada de esta sección aplica.
  const delAd = user.estado === 'DelDirectorio';
  // Ficha del padrón sin cuenta: su camino es el enlace de activación (el del registro), no la contraseña.
  const unclaimed = !user.reclamada && !delAd;
  const available = user.estado === 'Verificado' && user.reclamada && !delAd;
  const sendDisabled = unclaimed ? !user.correoPersonal : !available;

  let sendReason: string | undefined;
  let generateReason: string | undefined;
  if (delAd) {
    sendReason = t('frontlineUsers.err.directory');
    generateReason = sendReason;
  } else if (unclaimed) {
    if (!user.correoPersonal) sendReason = t('frontlineUsers.pw.needsEmail');
    generateReason = t('frontlineUsers.markUnclaimedTitle');
  } else if (!available) {
    sendReason = t('frontlineUsers.pw.needsVerified');
    generateReason = sendReason;
  }
  const visibleReasons = [...new Set([sendDisabled ? sendReason : undefined, generateReason].filter(Boolean))] as string[];
  return { delAd, unclaimed, available, sendDisabled, sendReason, generateReason, visibleReasons };
}

/**
 * Sección «Contraseña» del modal de edición: enviar el enlace de restablecimiento al correo personal y generar una
 * contraseña TEMPORAL.
 *
 * 🔴 La temporal es una credencial en claro que existe SOLO aquí, en estado local del componente: no va al
 * almacenamiento del navegador, ni a la URL, ni al caché de consultas (`gcTime: 0` en el hook), ni al registro; y se
 * descarta al cerrar el modal. Por eso el aviso «solo se muestra esta vez».
 *
 * Generar actúa AL INSTANTE, independiente de «Guardar cambios», y no se deshace al cancelar. Pero deja sin efecto la
 * contraseña actual de la persona, así que pide confirmación antes. El interruptor «pedir cambio en el próximo inicio de
 * sesión» va ANTES del botón (la maqueta lo mostraba después de generar, cuando ya no se podía aplicar).
 *
 * Solo se ofrece en cuentas ya reclamadas y con el correo verificado: sin cuenta no hay contraseña que reponer, y sin
 * correo verificado el login no la dejaría usar (el servidor repite estas reglas).
 */
export function PasswordSection({ user, onDone, onError }: Readonly<Props>) {
  const { t, i18n } = useTranslation();
  const generate = useGenerateTemporaryPassword();
  const sendLink = useSendFrontlinePasswordReset();
  const sendActivation = useSendFrontlineVerification();
  const [forceChange, setForceChange] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [generated, setGenerated] = useState<TemporaryPasswordDto | null>(null);

  // Al cerrar el modal el componente se desmonta: la contraseña y el resultado de la mutación se sueltan.
  const { reset: resetGenerate } = generate;
  useEffect(() => () => { resetGenerate(); }, [resetGenerate]);

  const { unclaimed, available, sendDisabled, sendReason, generateReason, visibleReasons } = passwordAvailability(user, t);

  const formatDate = (iso: string) => new Date(iso).toLocaleDateString(i18n.language, { dateStyle: 'medium' });

  let info = unclaimed ? t('frontlineUsers.pw.unclaimedNote') : t('frontlineUsers.pw.noRecord');
  if (user.contrasenaTemporalVenceEn) info = t('frontlineUsers.pw.tempPending', { date: formatDate(user.contrasenaTemporalVenceEn) });
  else if (!unclaimed && user.ultimoCambioPassword) info = t('frontlineUsers.pw.lastChange', { date: formatDate(user.ultimoCambioPassword) });

  const doSendLink = async () => {
    try {
      const result = await sendLink.mutateAsync(user.id);
      onDone(t('frontlineUsers.pw.linkSent', { email: result.enviadoA }));
    } catch (err) {
      onError(t(frontlineErrorKey(err)));
    }
  };

  const doSendActivation = async () => {
    try {
      const result = await sendActivation.mutateAsync(user.id);
      onDone(t('frontlineUsers.sentOk', { email: result.enviadoA }));
    } catch (err) {
      onError(t(frontlineErrorKey(err)));
    }
  };

  const doGenerate = async () => {
    setConfirming(false);
    try {
      setGenerated(await generate.mutateAsync({ id: user.id, forceChange }));
    } catch (err) {
      onError(t(frontlineErrorKey(err)));
    }
  };

  const copy = async () => {
    if (!generated) return;
    try {
      await navigator.clipboard.writeText(generated.password);
      onDone(t('frontlineUsers.pw.copied'));
    } catch {
      onError(t('frontlineUsers.pw.copyFailed'));
    }
  };

  return (
    <div className="gu-section">
      <div className="gu-section-title">{t('frontlineUsers.pw.title')}</div>
      <p className="gu-help">{info}</p>

      <label className="gu-check gu-check--block">
        <input type="checkbox" checked={forceChange} onChange={e => setForceChange(e.target.checked)} disabled={!available} />
        {t('frontlineUsers.pw.forceChange')}
      </label>
      {available && forceChange && <p className="gu-help">{t('frontlineUsers.pw.expiryHint')}</p>}

      <div className="gu-actions">
        <button type="button" className="btn btn-secondary gu-btn" disabled={sendDisabled || sendLink.isPending || sendActivation.isPending}
          title={sendDisabled ? sendReason : undefined} onClick={() => void (unclaimed ? doSendActivation() : doSendLink())}>
          <i className={`fas ${(sendLink.isPending || sendActivation.isPending) ? 'fa-spinner fa-spin' : 'fa-envelope'}`} /> {t(unclaimed ? 'frontlineUsers.pw.sendActivation' : 'frontlineUsers.pw.sendLink')}
        </button>
        <button type="button" className="btn btn-secondary gu-btn" disabled={!available || generate.isPending}
          title={available ? undefined : generateReason} onClick={() => setConfirming(true)}>
          <i className={`fas ${generate.isPending ? 'fa-spinner fa-spin' : 'fa-key'}`} /> {t('frontlineUsers.pw.generate')}
        </button>
      </div>
      {visibleReasons.map(reason => <p key={reason} className="gu-help gu-help--off">{reason}</p>)}
      {available && !generated && <p className="gu-help">{t('frontlineUsers.pw.generateNote')}</p>}

      {generated && (
        <div className="gu-temp" role="status">
          <div className="gu-temp-line">
            <code aria-label={t('frontlineUsers.event.PasswordTemporalGenerada')}>{generated.password}</code>
            <button type="button" className="btn btn-secondary" onClick={() => void copy()}>
              <i className="fas fa-copy" /> {t('frontlineUsers.pw.copy')}
            </button>
          </div>
          <p className="gu-help gu-help--warn">{t('frontlineUsers.pw.shownOnce')}</p>
          <p className="gu-help">
            {generated.expiresAt
              ? t('frontlineUsers.pw.expiresOn', { date: formatDate(generated.expiresAt) })
              : t('frontlineUsers.pw.noExpiry')}
          </p>
        </div>
      )}

      {confirming && (
        <ConfirmModal
          type="warning"
          title={t('frontlineUsers.pw.confirmTitle')}
          message={t('frontlineUsers.pw.confirmMsg')}
          confirmText={t('frontlineUsers.pw.generate')}
          cancelText={t('common.cancel')}
          onConfirm={() => void doGenerate()}
          onClose={() => setConfirming(false)}
        />
      )}
    </div>
  );
}
